import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { IoCheckmark } from 'react-icons/io5';
import { Select, SelectItem } from '../../../../../components/ui/Select';
import { inputSelectCustomStyles, StyledSelect } from '../../../../../components/CustomSearchBar';
import { ErrorText } from '../../../../../components/utils/AvatarCropper';
import { Palette } from '../../../../../components/utils';
import {
  buildScheduleStartValues,
  formatScheduleFractionalHour12h,
  getScheduleEndOptionValues,
  parseScheduleHour,
  selectedDaysToWeekChecks,
  snapDailyScheduleToNewSlotGrid,
  WEEK_DAY_CODES
} from './addAmenityScheduleHelpersV2';
import { FieldBlock, FieldInnerFull, FieldLabel } from '../../../../../components/WizardV2/wizardStylesV2';
import {
  SlotDurationIntro,
  SlotDurationSection,
  ScheduleDayLabelCell,
  ScheduleDayRow,
  ScheduleDayShortLabel,
  ScheduleDayToggleButton,
  ScheduleDayToggleMark,
  ScheduleFieldColumn,
  ScheduleTimesRow,
  ScheduleWeekGrid,
  ToggleDescription,
  ToggleTitle
} from './addAmenityWizardStylesV2';

const DAY_SHORT_LABELS = ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];

function AddAmenityScheduleStepV2({
  t,
  control,
  errors,
  amenitiesLabels,
  getValues,
  setValue,
  onApplyWeekChecks,
  scheduleMode = 'singleSlot',
  dayOptions
}) {
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  const selectedDays = useWatch({ control, name: 'selectedDays' });
  const slotDurationMinutes = useWatch({ control, name: 'slot_duration_minutes' }) ?? 60;
  const checks = useMemo(() => selectedDaysToWeekChecks(selectedDays), [selectedDays]);
  const dailySchedule = useWatch({ control, name: 'daily_schedule' }) || {};

  const prevSlotRef = useRef();
  useEffect(() => {
    if (prevSlotRef.current === undefined) {
      prevSlotRef.current = slotDurationMinutes;
      return;
    }
    if (prevSlotRef.current === slotDurationMinutes) return;
    prevSlotRef.current = slotDurationMinutes;
    const current = getValues('daily_schedule') || {};
    const { next, changed } = snapDailyScheduleToNewSlotGrid(current, slotDurationMinutes);
    if (changed) {
      setValue('daily_schedule', next, { shouldValidate: true, shouldDirty: true });
    }
  }, [slotDurationMinutes, getValues, setValue]);

  const startValues = useMemo(
    () => buildScheduleStartValues(slotDurationMinutes),
    [slotDurationMinutes]
  );

  const slotDurationOptions = useMemo(
    () => [
      { value: 30, label: t('add_amenity_slot_30_min', { defaultValue: '30 min' }) },
      { value: 60, label: t('add_amenity_slot_1_hr', { defaultValue: '1 hr' }) }
    ],
    [t]
  );

  const slotDurationSelectStyles = useMemo(() => {
    const base = inputSelectCustomStyles({ minHeight: '56px', height: 'auto' });
    if (!errors?.slot_duration_minutes) return base;
    return {
      ...base,
      control: (styles, state) => ({
        ...base.control(styles, state),
        border: `2px solid ${Palette.ERROR}`
      })
    };
  }, [errors?.slot_duration_minutes]);

  const toggleDay = (code) => {
    const next = { ...checks, [code]: !checks[code] };
    onApplyWeekChecks(next);
  };

  return (
    <>
      <SlotDurationSection>
        <SlotDurationIntro>
          <ToggleTitle>{t('Slot Duration')}</ToggleTitle>
          <ToggleDescription>
            {td(
              'add_amenity_slot_duration_description',
              'How long slots are for this amenity.'
            )}
          </ToggleDescription>
        </SlotDurationIntro>
        <FieldBlock>
          <FieldInnerFull>
            <FieldLabel>
              {t('add_amenity_slot_interval', { defaultValue: 'Slot Interval' })}
            </FieldLabel>
            <Controller
              control={control}
              name="slot_duration_minutes"
              render={({ field: { onChange, onBlur, value, ref } }) => {
                const numeric = value != null && value !== '' ? Number(value) : 60;
                const selected =
                  slotDurationOptions.find((o) => o.value === numeric) || slotDurationOptions[0];
                return (
                  <StyledSelect
                    ref={ref}
                    inputId="wizard-schedule-slot_duration_minutes"
                    instanceId="amenity-slot-duration-schedule"
                    options={slotDurationOptions}
                    value={selected}
                    onChange={(opt) => onChange(opt != null ? opt.value : 60)}
                    onBlur={onBlur}
                    styles={slotDurationSelectStyles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable={false}
                    isSearchable={false}
                    menuPlacement="auto"
                  />
                );
              }}
            />
          </FieldInnerFull>
          {errors?.slot_duration_minutes && (
            <ErrorText>{errors.slot_duration_minutes.message}</ErrorText>
          )}
        </FieldBlock>
      </SlotDurationSection>

      <ScheduleWeekGrid data-schedule-mode={scheduleMode}>
        {WEEK_DAY_CODES.map((code, i) => {
          const startErr = errors?.daily_schedule?.[code]?.start;
          const endErr = errors?.daily_schedule?.[code]?.end;
          return (
            <ScheduleDayRow key={code}>
              <ScheduleDayLabelCell>
                <ScheduleDayToggleButton
                  type="button"
                  aria-label={
                    dayOptions?.[i]?.label
                      ? t('amenity_schedule_toggle_day', {
                          defaultValue: 'Include {{day}}',
                          day: dayOptions[i].label
                        })
                      : DAY_SHORT_LABELS[i]
                  }
                  aria-pressed={checks[code]}
                  onClick={() => toggleDay(code)}>
                  <ScheduleDayToggleMark $checked={checks[code]}>
                    {checks[code] ? (
                      <IoCheckmark color={Palette.WHITE} size={14} aria-hidden />
                    ) : null}
                  </ScheduleDayToggleMark>
                </ScheduleDayToggleButton>
                <ScheduleDayShortLabel>{DAY_SHORT_LABELS[i]}</ScheduleDayShortLabel>
              </ScheduleDayLabelCell>

              <ScheduleTimesRow>
                <ScheduleFieldColumn>
                  <FieldLabel>{amenitiesLabels.start_time}</FieldLabel>
                  <Controller
                    control={control}
                    name={`daily_schedule.${code}.start`}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? undefined}
                        onChange={(v) => {
                          field.onChange(v);
                          const startNum = parseScheduleHour(v);
                          const endVal = getValues(`daily_schedule.${code}.end`);
                          const endNum = parseScheduleHour(endVal);
                          if (startNum == null || endNum == null) return;
                          const opts = getScheduleEndOptionValues(
                            startNum,
                            slotDurationMinutes
                          );
                          const stillValid = opts.some(
                            (o) => Math.abs(o - endNum) < 1e-5
                          );
                          if (!stillValid) {
                            setValue(`daily_schedule.${code}.end`, null, {
                              shouldValidate: true,
                              shouldDirty: true
                            });
                          }
                        }}
                        placeholder={t('Please select')}
                        custom
                        valid={!startErr}
                        invalid={!!startErr}
                        placement="bottomRight"
                        id={`schedule-${code}-start`}>
                        <SelectItem value={-1}>{t('Please select')}</SelectItem>
                        {startValues.map((hour) => (
                          <SelectItem key={hour} value={hour}>
                            {formatScheduleFractionalHour12h(hour)}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                  />
                  {startErr?.message ? <ErrorText>{startErr.message}</ErrorText> : null}
                </ScheduleFieldColumn>
                <ScheduleFieldColumn>
                  <FieldLabel>{amenitiesLabels.end_time}</FieldLabel>
                  <Controller
                    control={control}
                    name={`daily_schedule.${code}.end`}
                    render={({ field }) => {
                      const startVal = dailySchedule?.[code]?.start;
                      const startNum = parseScheduleHour(startVal);
                      const endHourOptions =
                        startNum != null
                          ? getScheduleEndOptionValues(startNum, slotDurationMinutes)
                          : [];

                      return (
                        <Select
                          {...field}
                          value={field.value ?? undefined}
                          placeholder={t('Please select')}
                          custom
                          valid={!endErr}
                          invalid={!!endErr}
                          placement="bottomLeft"
                          id={`schedule-${code}-end`}>
                          <SelectItem value={-1}>{t('Please select')}</SelectItem>
                          {endHourOptions.map((hour) => (
                            <SelectItem key={hour} value={hour}>
                              {formatScheduleFractionalHour12h(hour)}
                            </SelectItem>
                          ))}
                        </Select>
                      );
                    }}
                  />
                  {endErr?.message ? <ErrorText>{endErr.message}</ErrorText> : null}
                </ScheduleFieldColumn>
              </ScheduleTimesRow>
            </ScheduleDayRow>
          );
        })}
      </ScheduleWeekGrid>

      {errors?.selectedDays && <ErrorText>{errors.selectedDays.message}</ErrorText>}
    </>
  );
}

export default AddAmenityScheduleStepV2;
